import { useEffect, useMemo, useState } from "react";
import { Calendar as CalIcon, Sun, Sunset, CheckCircle2, ArrowRight, Loader2, Info } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { track, fbEvent } from "@/lib/analytics";
import SEO, { breadcrumbJsonLd } from "@/components/SEO";

function pad(n) { return String(n).padStart(2, "0"); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function buildMonth(year, month) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOWS = ["S", "M", "T", "W", "T", "F", "S"];

export default function Book() {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const [viewMonth, setViewMonth] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState({ available: [] });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", topic: "Behavioral Health Consulting", notes: "" });
  const [state, setState] = useState({ loading: false, success: false, error: "", bookingId: null });

  const PERIOD_LABEL = { morning: "Morning", afternoon: "Afternoon" };
  const prettyPeriod = selectedTime ? PERIOD_LABEL[selectedTime] || selectedTime : "";

  const cells = buildMonth(viewMonth.y, viewMonth.m);

  const goPrev = () => {
    const d = new Date(viewMonth.y, viewMonth.m - 1, 1);
    if (d < new Date(today.getFullYear(), today.getMonth(), 1)) return;
    setViewMonth({ y: d.getFullYear(), m: d.getMonth() });
  };
  const goNext = () => {
    const d = new Date(viewMonth.y, viewMonth.m + 1, 1);
    setViewMonth({ y: d.getFullYear(), m: d.getMonth() });
  };

  const selectDate = async (d) => {
    if (!d) return;
    // no weekends
    if (d.getDay() === 0 || d.getDay() === 6) return;
    if (d < today) return;
    const ds = toDateStr(d);
    setSelectedDate(ds);
    setSelectedTime(null);
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/bookings/availability?date=${ds}`);
      setAvailability(data);
    } catch {
      setAvailability({ available: [] });
    } finally {
      setLoadingSlots(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setState({ loading: true, success: false, error: "", bookingId: null });
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        slot_date: selectedDate,
        slot_time: selectedTime,
      });
      track("book_consultation", { topic: form.topic, slot_time: selectedTime });
      fbEvent("Lead", { content_name: "Consultation Booking", content_category: form.topic });
      setState({ loading: false, success: true, error: "", bookingId: data.id });
    } catch (err) {
      setState({ loading: false, success: false, error: formatApiError(err), bookingId: null });
    }
  };

  const isPast = (d) => d && d < today;
  const isWeekend = (d) => d && (d.getDay() === 0 || d.getDay() === 6);
  const isSelected = (d) => d && toDateStr(d) === selectedDate;

  if (state.success) {
    return (
      <SiteLayout branch="main">
        <section className="section max-w-2xl mx-auto text-center" data-testid="booking-success-screen">
          <CheckCircle2 className="mx-auto text-green-600" size={56} />
          <h1 className="font-display text-4xl text-[var(--navy)] mt-6">Request received.</h1>
          <p className="text-[var(--muted)] mt-4">
            One of our consultants will reach out to <strong className="text-[var(--navy)]">{form.email}</strong> shortly
            to lock in a specific time within your preferred window.
          </p>
          <div className="border border-[var(--line)] bg-white p-6 mt-8 text-left inline-block">
            <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">YOUR PREFERENCE</div>
            <div className="font-display text-2xl text-[var(--navy)]">{selectedDate} · {prettyPeriod}</div>
            <div className="text-sm text-[var(--muted)] mt-2">Topic: {form.topic}</div>
          </div>
          <div className="mt-8">
            <a href="/" className="btn btn-navy">Back to Home</a>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="main">
      <SEO
        title="Book a Consultation"
        path="/book"
        description="Book a free 30-minute discovery consultation with Catalyst BH Consulting — behavioral health, HR, and program development advisors."
        jsonLd={breadcrumbJsonLd([["Home", "/"], ["Book a Consultation"]])}
      />
      {/* HERO */}
      <section className="grain relative bg-[var(--navy)] text-white px-[6vw] py-24" data-testid="book-hero">
        <Reveal className="max-w-3xl">
          <span className="eyebrow">BOOK A CONSULTATION</span>
          <h1 className="font-display mt-5 text-[clamp(40px,5vw,70px)] leading-[1.04]">
            A 30-minute call to explore <em className="italic text-[var(--gold)]">what's possible.</em>
          </h1>
          <p className="text-neutral-300 text-[17px] leading-[1.75] mt-6">
            Share your preferred day and window — one of our consultants will reach out to lock in a
            specific time. No pressure. No script.
          </p>
        </Reveal>
      </section>

      {/* BOOKING */}
      <section className="section bg-[var(--light)]">
        <div className="max-w-[1250px] mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10">
          {/* Calendar + Slots */}
          <Reveal className="bg-white border border-[var(--line)] p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">STEP 1</div>
                <h2 className="font-display text-2xl text-[var(--navy)] flex items-center gap-2">
                  <CalIcon size={20} /> Pick a date
                </h2>
              </div>
              <div className="flex gap-2">
                <button onClick={goPrev} className="w-9 h-9 border border-[var(--line)] hover:bg-[var(--cream)]" data-testid="cal-prev">‹</button>
                <button onClick={goNext} className="w-9 h-9 border border-[var(--line)] hover:bg-[var(--cream)]" data-testid="cal-next">›</button>
              </div>
            </div>
            <div className="font-display text-xl text-[var(--navy)] mb-4">{MONTHS[viewMonth.m]} {viewMonth.y}</div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DOWS.map((d, i) => (
                <div key={i} className="text-center text-[10px] tracking-widest text-[var(--muted)] font-bold py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {cells.map((d, i) => {
                const disabled = !d || isPast(d) || isWeekend(d);
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => selectDate(d)}
                    className={`aspect-square text-sm font-semibold transition-colors ${
                      !d ? "invisible"
                        : isSelected(d) ? "bg-[var(--navy)] text-white"
                        : disabled ? "text-neutral-300 cursor-not-allowed"
                        : "hover:bg-[var(--cream)] text-[var(--navy)]"
                    }`}
                    data-testid={d ? `date-${toDateStr(d)}` : undefined}
                  >
                    {d ? d.getDate() : ""}
                  </button>
                );
              })}
            </div>

            {selectedDate && (
              <div className="mt-8 pt-8 border-t border-[var(--line)]">
                <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">STEP 2</div>
                <h3 className="font-display text-2xl text-[var(--navy)] flex items-center gap-2 mb-2">
                  <Sun size={20} /> Pick a window
                </h3>
                <p className="text-[var(--muted)] text-sm mb-5 flex items-start gap-2">
                  <Info size={14} className="mt-[3px] flex-shrink-0 text-[var(--gold)]" />
                  <span>One of our consultants will reach out to confirm a specific time within your chosen window.</span>
                </p>
                {loadingSlots ? (
                  <div className="text-[var(--muted)] text-sm">Loading availability…</div>
                ) : availability.available.length === 0 ? (
                  <div className="text-[var(--muted)] text-sm">Not available on this day. Please pick another.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-4" data-testid="time-slots">
                    {availability.available.includes("morning") && (
                      <button
                        onClick={() => setSelectedTime("morning")}
                        className={`p-6 border-2 text-left transition-all group ${
                          selectedTime === "morning"
                            ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                            : "bg-white border-[var(--line)] text-[var(--navy)] hover:border-[var(--navy)]"
                        }`}
                        data-testid="slot-morning"
                      >
                        <Sun size={26} className={`mb-2 ${selectedTime === "morning" ? "text-[var(--gold)]" : "text-[var(--gold)]"}`} />
                        <div className="font-display text-2xl">Morning</div>
                        <div className={`text-xs tracking-widest mt-1 ${selectedTime === "morning" ? "text-neutral-300" : "text-[var(--muted)]"}`}>
                          BEFORE 12 PM
                        </div>
                      </button>
                    )}
                    {availability.available.includes("afternoon") && (
                      <button
                        onClick={() => setSelectedTime("afternoon")}
                        className={`p-6 border-2 text-left transition-all group ${
                          selectedTime === "afternoon"
                            ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                            : "bg-white border-[var(--line)] text-[var(--navy)] hover:border-[var(--navy)]"
                        }`}
                        data-testid="slot-afternoon"
                      >
                        <Sunset size={26} className={`mb-2 text-[var(--gold)]`} />
                        <div className="font-display text-2xl">Afternoon</div>
                        <div className={`text-xs tracking-widest mt-1 ${selectedTime === "afternoon" ? "text-neutral-300" : "text-[var(--muted)]"}`}>
                          12 PM – 5 PM
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </Reveal>

          {/* Form */}
          <Reveal>
            <form onSubmit={submit} className="bg-white border border-[var(--line)] p-8" data-testid="booking-form">
              <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">STEP 3</div>
              <h2 className="font-display text-2xl text-[var(--navy)] mb-6">Your details</h2>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="field">
                  <label className="field-label">Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name" />
                </div>
                <div className="field">
                  <label className="field-label">Organization</label>
                  <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="book-org" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="field">
                  <label className="field-label">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" />
                </div>
                <div className="field">
                  <label className="field-label">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" />
                </div>
              </div>
              <div className="field mb-4">
                <label className="field-label">Topic</label>
                <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="book-topic">
                  <option>Behavioral Health Consulting</option>
                  <option>HR Consulting</option>
                  <option>Catalyst Continuum</option>
                  <option>Academy / Digital Resources</option>
                  <option>Executive Coaching</option>
                </select>
              </div>
              <div className="field mb-6">
                <label className="field-label">Anything we should know?</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="book-notes" />
              </div>

              <div className="bg-[var(--cream)] border border-[var(--line)] p-4 mb-6 text-sm">
                <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">YOUR PREFERENCE</div>
                {selectedDate && selectedTime ? (
                  <div className="font-display text-lg text-[var(--navy)]" data-testid="selected-slot">
                    {selectedDate} · {prettyPeriod}
                  </div>
                ) : (
                  <div className="text-[var(--muted)]">Select a date and window on the left.</div>
                )}
                <p className="text-[11px] text-[var(--muted)] mt-2 leading-relaxed">
                  A consultant will call to lock in a specific time within your preferred window.
                </p>
              </div>

              <button
                type="submit"
                disabled={!selectedDate || !selectedTime || state.loading}
                className="btn btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="book-submit"
              >
                {state.loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Sending…</> : (
                  <>Request Consultation <ArrowRight size={14} className="ml-2" /></>
                )}
              </button>
              {state.error && <p className="text-red-700 text-xs mt-4" data-testid="booking-error">{state.error}</p>}
            </form>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
